import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_LOGOUT_EVENT = 'auth:logout';
const AUTH_TOKEN_REFRESHED_EVENT = 'auth:token-refreshed';

// Public API client for endpoints that must not trigger token refresh loops.
export const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

// Authenticated API client that refreshes tokens once before retrying expired-token requests.
const api = axios.create({
  baseURL: API_BASE_URL,
});

let refreshRequest = null;

/**
 * shouldSkipRefresh - Prevents public auth/token endpoints from triggering refresh loops.
 */
const shouldSkipRefresh = (url = '') => {
  const isResetPasswordTokenEndpoint = url.includes('/api/v1/auth/reset-password/');
  const isInviteTokenEndpoint = url.includes('/api/v1/invites/') && (
    url.includes('/validate') || url.includes('/accept')
  );

  return [
    '/api/v1/auth/login',
    '/api/v1/auth/logout',
    '/api/v1/auth/refresh',
    '/api/v1/auth/forgot-password',
  ].some((path) => url.includes(path)) || isResetPasswordTokenEndpoint || isInviteTokenEndpoint;
};

/**
 * clearAuthSession - Clears local auth and lets AuthContext react to the logout state.
 */
const clearAuthSession = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('username');
  window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT));
};

/**
 * refreshTokens - Exchanges the refresh token for a new token pair.
 */
const refreshTokens = async () => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    clearAuthSession();
    throw new Error('Missing refresh token');
  }

  const response = await publicApi.post('/api/v1/auth/refresh', {
    refresh_token: refreshToken,
  });

  localStorage.setItem('access_token', response.data.access_token);
  localStorage.setItem('refresh_token', response.data.refresh_token);

  // Tell AuthContext to use the refreshed access token without requiring a page reload.
  window.dispatchEvent(new CustomEvent(AUTH_TOKEN_REFRESHED_EVENT, {
    detail: { access_token: response.data.access_token },
  }));

  return response.data.access_token;
};

/**
 * installRefreshInterceptors - Adds auth headers and one-shot refresh retry behavior to an Axios client.
 */
const installRefreshInterceptors = (client) => {
  // Reads the latest access token immediately before each authenticated request.
  client.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem('access_token');

    if (accessToken && !shouldSkipRefresh(config.url)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status !== 401 ||
        originalRequest?._retry ||
        shouldSkipRefresh(originalRequest?.url)
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Share one refresh request across concurrent 401 responses.
        refreshRequest = refreshRequest || refreshTokens();
        const newAccessToken = await refreshRequest;
        refreshRequest = null;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return client(originalRequest);
      } catch (refreshError) {
        refreshRequest = null;
        clearAuthSession();
        return Promise.reject(refreshError);
      }
    }
  );
};

installRefreshInterceptors(api);
installRefreshInterceptors(axios);

export { AUTH_LOGOUT_EVENT, AUTH_TOKEN_REFRESHED_EVENT };
export default api;
